/**
 * House identity — 익명 유저 식별. localStorage 에 houseUuid 저장, 모든 보호된 API 에 헤더로 실어 보낸다.
 * 스토리지 키는 rpg-web 과 호환 — 기존 유저가 새 클라이언트로 자연스럽게 넘어올 수 있게.
 */

export const HOUSE_UUID_STORAGE_KEY = "rpg.houseUuid";
export const HOUSE_NAME_STORAGE_KEY = "rpg.houseName";
export const HOUSE_UUID_HEADER = "X-House-Uuid";

export interface HouseIdentity {
    houseUuid: string;
    houseName: string;
}

/**
 * 2026-04-23: 기존 strict V4 regex (`...4[0-9a-f]{3}-[89ab]...`) 는 서버가 V1/V5 등
 *   다른 버전 UUID 를 주면 실패 → loadHouseIdentity 가 null 반환 → getHouseUuid null
 *   → mergeHeaders 가 X-House-Uuid 헤더를 안 붙여 IDENTITY_MISSING 발생.
 *   format 자체 (8-4-4-4-12) 만 확인하는 관대한 regex 로 완화.
 */
const UUID_ANY = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function browserStorage(): Storage | null {
    if (typeof window === "undefined") return null;
    try { return window.localStorage; } catch { return null; }
}

export function isUuidV4(value: string | null | undefined): boolean {
    if (!value) return false;
    return UUID_ANY.test(value);
}

export function loadHouseIdentity(): HouseIdentity | null {
    const store = browserStorage();
    if (!store) return null;
    const uuid = store.getItem(HOUSE_UUID_STORAGE_KEY);
    const name = store.getItem(HOUSE_NAME_STORAGE_KEY);
    if (!uuid || !name || !isUuidV4(uuid)) return null;
    return { houseUuid: uuid, houseName: name };
}

export function saveHouseIdentity(identity: HouseIdentity): void {
    const store = browserStorage();
    if (!store) return;
    store.setItem(HOUSE_UUID_STORAGE_KEY, identity.houseUuid);
    store.setItem(HOUSE_NAME_STORAGE_KEY, identity.houseName);
}

export function clearHouseIdentity(): void {
    const store = browserStorage();
    if (!store) return;
    store.removeItem(HOUSE_UUID_STORAGE_KEY);
    store.removeItem(HOUSE_NAME_STORAGE_KEY);
}

export function getHouseUuid(): string | null {
    return loadHouseIdentity()?.houseUuid ?? null;
}
